import { AccountFormWrapper, Form, H2, Text } from 'pages/AccountView/components/TextElements'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { get_error_modal_props } from 'app-view/components/GlobalModal/lib/utility'
import { createWebAuthNKey } from 'app-engine/library/eosio/webauthn'
import { RegistrationData } from 'app-engine/types/registration'
import { HintError, Input, InputSection, InputWrapper, Label } from 'app-view/components/InputField'
import { AccessLoader } from 'app-view/components/Spinner'
import { useAppleWarning } from 'app-view/hooks/useIosWarning'
import { LargeBlackButton, LargeGreenButton } from 'pages/AccountView/components/LargeButtons'
import { useRegistration } from 'pages/AccountView/hooks/useRegistration'
import { checkAccountExt } from 'pages/AccountView/utils'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useGlobalModal } from 'app-view/components/GlobalModal/useGlobalModal'
import { ModalError } from 'app-view/components/Modal/Error'
import { config } from 'app-config'
import * as Bitcash from 'app-engine/graphql/generated/bitcash'

type StepData = { deviceName: string }

export const CreateAccountWebAuthNCred = () => {
  const { t } = useTranslation(['account', 'global'])
  const [{ account, link, referrer }, { setRegistrationData }] = useRegistration()
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)
  const openAppleWarning = useAppleWarning()
  const [, globalModalActions] = useGlobalModal()
  const [requestNewAccount] = Bitcash.useBitcashRequestNewAccountMutation()
  const [updateAccountReferralRegisterCount] =
    Bitcash.useUpdateAccountReferralRegisterCountMutation()

  const form = useForm<StepData>({
    defaultValues: {
      deviceName: `${account}'s main device`,
    },
  })

  const onSubmit = useCallback(
    async ({ deviceName }: StepData) => {
      setLoading(true)

      openAppleWarning(async () => {
        try {
          setLoading(true)

          const formattedAccount = checkAccountExt(account)
          const { pubKey: public_key, credId: cred_id } = await createWebAuthNKey(formattedAccount)

          setRegistrationData({
            public_key,
            cred_id,
            device_name: deviceName,
          } as Partial<RegistrationData>)

          const formattedReferrer = checkAccountExt(referrer as string)
          console.log('webauthn credentials', { public_key, cred_id })

          if (config.features.kyc) {
            navigate('/create-account/kyc')
          } else {
            const newAccountRequested = await requestNewAccount({
              variables: {
                account_data: {
                  country_id: {
                    id: '',
                    type: '',
                    country: '',
                  },
                  account: formattedAccount,
                  referrer: formattedReferrer,
                  cred_id: cred_id || '',
                  public_key: public_key,
                  full_name: '',
                  device_name: deviceName,
                  phone_number: '',
                  email_address: '',
                  date_of_birth: '',
                  newsletter_subscription: false,
                  create_account: true,
                  source: '',
                  selfie: '',
                  photo_id: '',
                } as Bitcash.Request_New_Account_Input,
              },
            })
    
            if (link) {
              const incRegisterCount = await updateAccountReferralRegisterCount({
                variables: {
                  link,
                },
              })
              console.log('registring account response::incRegisterCount', incRegisterCount)
            }
    
            console.log('registring account response::newAccountRequested', newAccountRequested)
    
            if (newAccountRequested.errors) throw new Error(newAccountRequested.errors[0].message)
            setLoading(false)
            navigate('/create-account/success')
          }

          setLoading(false)
        } catch (err) {
          setLoading(false)
          globalModalActions.open(
            get_error_modal_props(() => <ModalError error={(err as Error).message} />),
          )
        }
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openAppleWarning, account, setRegistrationData, globalModalActions], // navigate is not necessary it is a stable function
  )

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)}>
      <AccountFormWrapper>
        <div>
          <H2>{t('create_account_key_title')}</H2>
          <Text
            dangerouslySetInnerHTML={{
              __html: t('create_account_key_description'),
            }}
          />
        </div>
        <Controller
          name="deviceName"
          control={form.control}
          rules={{
            required: t('create_account_key_device_error_required') as string,
          }}
          render={({ field, fieldState }) => (
            <InputWrapper mt={32}>
              <Label size="lg" pl={0}>
                {t('create_account_key_device_label')}
              </Label>
              <InputSection inputSize="sm">
                <Input
                  {...field}
                  placeholder={t('create_account_key_device_placeholder')}
                  fullradius={1}
                  fontWeight="500"
                  error={Boolean(!form.formState.isValidating && fieldState.error)}
                />
              </InputSection>
              {!form.formState.isValidating && fieldState.error && (
                <HintError align="left">{fieldState.error.message}</HintError>
              )}
            </InputWrapper>
          )}
        />
      </AccountFormWrapper>
      {loading && <AccessLoader />}
      <div>
        <LargeGreenButton
          type="submit"
          disabled={form.formState.isValidating || form.formState.isSubmitting || loading}
        >
          {t('create_account_key_submit')}
        </LargeGreenButton>
        <LargeBlackButton type="button" onClick={() => navigate(-1)} disabled={loading}>
          {t('global:back')}
        </LargeBlackButton>
      </div>
    </Form>
  )
}
